import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { 
  User, Menu, Loader2, Bell
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';
import { Button } from '../components/ui/button';
import AssistantDuBug from '../components/shared/AssistantDuBug';
import GoogleReviewPopup from '../components/shared/GoogleReviewPopup';
import { ProactiveChatBubble } from '../components/shared/ProactiveChatBubble';
import usePushNotifications from '../hooks/usePushNotifications';

// V5 REFONDATION : Import de la Sidebar Contextuelle
import { ContextualSidebar, MobileMenu } from '../components/layout/ContextualSidebar';
import { normalizeMissionType } from '../components/missions/shared/MissionComponents';

// V5.27 : Import de la fonction de nettoyage depuis le gestionnaire centralisé
import { clearPreviousData } from '../utils/missionDataManager';

// Import extracted tab components
import {
  OverviewTab,
  MissionsTab,
  PaymentTab,
  MissionFormTab,
  MandatTab,
  DocumentsTab,
  AuditTab,
  DataSecurityTab,
  ReferralTab,
  ProofsTab,
  SupportTab
} from '../components/dashboard/tabs';
import EmailVerificationBanner from '../components/dashboard/EmailVerificationBanner';

// Main Dashboard
export default function DashboardPage() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // CCC.11 — F5 ne reset plus à l'accueil. On lit la dernière tab depuis localStorage
  // (mais l'URL ?tab= prime sur le localStorage si présente).
  const [activeTab, setActiveTab] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('tab')) return 'overview'; // sera réécrit par useEffect ci-dessous
      return localStorage.getItem('dashboard_active_tab') || 'overview';
    } catch {
      return 'overview';
    }
  });
  // Persiste à chaque changement de tab
  useEffect(() => {
    try { localStorage.setItem('dashboard_active_tab', activeTab); } catch {}
  }, [activeTab]);
  const [documents, setDocuments] = useState([]);
  const [mandats, setMandats] = useState([]);
  const [activeSubscription, setActiveSubscription] = useState(null);
  const [missionStatus, setMissionStatus] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [chatbotOpen, setChatbotOpen] = useState(false);
  const [showReviewPopup, setShowReviewPopup] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  
  // 🎫 TICKET #02 : Gérer le paramètre ?tab= dans l'URL (ex: ?tab=audit)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get('tab');
    if (tabParam) {
      // Mapper les noms de tab depuis l'URL
      // V5.7 : 'missions' affiche maintenant l'onglet MissionsTab (liste des cartes)
      const tabMapping = {
        'audit': 'audit',
        'audit-ia': 'audit',
        'overview': 'overview',
        'apercu': 'overview',
        'mission': 'mission-form',
        'missions': 'missions',  // V5.7 : Corrigé - affiche les cartes de mission
        'mes-missions': 'missions',  // V5.7 : Alias
        'mission-form': 'mission-form',
        'demenagement': 'mission-form',
        'mandat': 'mandat',
        'documents': 'documents',
        'parrainage': 'referral',
        'referral': 'referral',
        'preuves': 'proofs',
        'proofs': 'proofs',
        'securite': 'data-security',
        'data-security': 'data-security',
        'paiement': 'payment',
        'payment': 'payment'
      };
      const mappedTab = tabMapping[tabParam.toLowerCase()];
      if (mappedTab) {
        setActiveTab(mappedTab);
      }
    }
  }, [location.search]);
  
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) { navigate('/login'); return; }
    
    // V5.25 : Nettoyage atomique du cache au chargement du Dashboard
    // Cela garantit un état propre avant de démarrer une nouvelle mission
    clearPreviousData();
    
    const fetchData = async () => {
      try {
        const headers = { 'Authorization': `Bearer ${token}` };
        const [userRes, docsRes, mandatsRes, subRes, missionRes] = await Promise.all([
          axios.get(`${API}/auth/me`, { headers, withCredentials: true }),
          axios.get(`${API}/documents`, { headers, withCredentials: true }),
          axios.get(`${API}/mandats`, { headers, withCredentials: true }),
          axios.get(`${API}/subscriptions/active`, { headers, withCredentials: true }),
          axios.get(`${API}/missions/my-status`, { headers, withCredentials: true }).catch(() => ({ data: { has_mission: false } }))
        ]);
        setUser(userRes.data.user);
        setDocuments(docsRes.data.documents || []);
        setMandats(mandatsRes.data.mandats || []);
        setActiveSubscription(subRes.data.subscription);
        setMissionStatus(missionRes.data);
        
        // Show onboarding for new users is handled by ProactiveChatBubble component
        // which triggers automatically for users without documents
        
        // Check if we should show Google Review popup (mission just closed)
        try {
          const reviewRes = await axios.get(`${API}/data-retention/check-closed-status`, { headers, withCredentials: true });
          if (reviewRes.data.show_review_popup) {
            setShowReviewPopup(true);
          }
        } catch (e) {
          // Silent fail - not critical
        }
      } catch (error) {
        if (error.response?.status === 401) { localStorage.removeItem('token'); navigate('/login'); }
      } finally { setLoading(false); }
    };
    fetchData();
    
    // Handle payment success/cancel notifications
    const params = new URLSearchParams(location.search);
    if (params.get('payment') === 'success') { 
      toast.success('🎉 Paiement confirmé ! Votre pack est maintenant actif.'); 
      window.history.replaceState({}, '', '/dashboard'); 
    }
    if (params.get('payment') === 'cancelled') { 
      toast.error('Paiement annulé. Vous pouvez réessayer depuis l\'onglet Paiement.'); 
      window.history.replaceState({}, '', '/dashboard'); 
    }
    if (params.get('preauth') === 'success') { 
      toast.success('🔒 Pré-autorisation réussie ! Le montant est bloqué. Vos résiliations sont en cours de traitement.'); 
      window.history.replaceState({}, '', '/dashboard'); 
    }
    if (params.get('preauth') === 'cancelled') { 
      toast.error('Pré-autorisation annulée'); 
      window.history.replaceState({}, '', '/dashboard'); 
    }
    if (params.get('cancellation') === 'success') { 
      toast.success('Commission payée - Résiliations en cours !'); 
      window.history.replaceState({}, '', '/dashboard'); 
    }
  }, [navigate, location]);
  
  // Push notifications hook
  const pushNotifications = usePushNotifications();
  
  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('isAdmin');
    localStorage.removeItem('userEmail');
    navigate('/');
  };
  
  const handlePaymentSuccess = async () => {
    const token = localStorage.getItem('token');
    const subRes = await axios.get(`${API}/subscriptions/active`, { headers: { 'Authorization': `Bearer ${token}` }, withCredentials: true });
    setActiveSubscription(subRes.data.subscription);
    setActiveTab('mandat');
  };
  
  const handlePanicRoom = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/documents/panic-room/all`, { headers: { 'Authorization': `Bearer ${token}` }, withCredentials: true });
      setDocuments([]);
      toast.success('Tous les documents supprimés');
    } catch { toast.error('Erreur'); }
  };
  
  if (loading) {
    return <div className="min-h-screen bg-void flex items-center justify-center"><Loader2 className="w-12 h-12 text-cyan-400 animate-spin" /></div>;
  }
  
  return (
    <div className="min-h-screen bg-void flex overflow-x-hidden relative">
      {/* Background Circuit Animation */}
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden opacity-10">
        <svg className="w-full h-full" viewBox="0 0 1920 1080" preserveAspectRatio="xMidYMid slice">
          <defs>
            <linearGradient id="dash-circuit-cyan" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#00F5FF" stopOpacity="0" />
              <stop offset="50%" stopColor="#00F5FF" stopOpacity="0.8" />
              <stop offset="100%" stopColor="#00F5FF" stopOpacity="0" />
            </linearGradient>
          </defs>
          <path d="M0 200 L300 200 L350 150 L500 150 L550 200 L800 200" stroke="url(#dash-circuit-cyan)" strokeWidth="1" fill="none">
            <animate attributeName="stroke-dasharray" from="0 2000" to="2000 0" dur="8s" repeatCount="indefinite" />
          </path>
          <path d="M1920 400 L1620 400 L1570 350 L1420 350 L1370 400 L1120 400" stroke="url(#dash-circuit-cyan)" strokeWidth="1" fill="none">
            <animate attributeName="stroke-dasharray" from="0 2000" to="2000 0" dur="10s" repeatCount="indefinite" />
          </path>
          <path d="M0 600 L400 600 L450 650 L600 650 L650 600 L900 600" stroke="url(#dash-circuit-cyan)" strokeWidth="1" fill="none">
            <animate attributeName="stroke-dasharray" from="0 2000" to="2000 0" dur="7s" repeatCount="indefinite" />
          </path>
          <path d="M1920 800 L1500 800 L1450 750 L1300 750 L1250 800 L1000 800" stroke="url(#dash-circuit-cyan)" strokeWidth="1" fill="none">
            <animate attributeName="stroke-dasharray" from="0 2000" to="2000 0" dur="9s" repeatCount="indefinite" />
          </path>
          <circle cx="350" cy="150" r="3" fill="#00F5FF" opacity="0.5"><animate attributeName="opacity" values="0.2;0.8;0.2" dur="3s" repeatCount="indefinite" /></circle>
          <circle cx="1570" cy="350" r="3" fill="#00F5FF" opacity="0.5"><animate attributeName="opacity" values="0.3;0.9;0.3" dur="2.5s" repeatCount="indefinite" /></circle>
          <circle cx="450" cy="650" r="3" fill="#00F5FF" opacity="0.5"><animate attributeName="opacity" values="0.2;0.7;0.2" dur="4s" repeatCount="indefinite" /></circle>
          <circle cx="1450" cy="750" r="3" fill="#00F5FF" opacity="0.5"><animate attributeName="opacity" values="0.3;0.8;0.3" dur="3.5s" repeatCount="indefinite" /></circle>
        </svg>
      </div>
      
      {/* Mobile Menu - V5 Refondation */}
      <MobileMenu 
        activeTab={activeTab} 
        setActiveTab={setActiveTab} 
        user={user} 
        onLogout={handleLogout} 
        isOpen={mobileMenuOpen} 
        setIsOpen={setMobileMenuOpen}
        context="dashboard"
      />
      
      {/* Desktop Sidebar - V5 Refondation : Sidebar Contextuelle */}
      <ContextualSidebar 
        context="dashboard"
        activeTab={activeTab} 
        setActiveTab={setActiveTab} 
        user={user} 
        onLogout={handleLogout} 
        activeSubscription={activeSubscription} 
        pushNotifications={pushNotifications} 
      />
      
      {/* Main Content — CCC.13 : retiré overflow-y-auto pour que sidebar sticky suive le scroll */}
      <main className="flex-1 p-4 md:p-8 pb-24 min-w-0">
        {/* Mobile Header */}
        <div className="md:hidden flex items-center justify-between mb-6">
          <Button variant="ghost" onClick={() => setMobileMenuOpen(true)} className="text-gray-400 p-2" data-testid="mobile-menu-btn">
            <Menu className="w-6 h-6" />
          </Button>
          <Link to="/" className="font-orbitron text-lg font-bold text-cyan-400">L'EFFACEUR</Link>
          <Link to="/profile">
            <Button variant="ghost" className="text-gray-400 p-2"><User className="w-6 h-6" /></Button>
          </Link>
        </div>
        
        {/* Email verification banner — affichée si email pas vérifié */}
        <EmailVerificationBanner />

        {/* Tab Content */}
        {activeTab === 'overview' && <OverviewTab user={user} documents={documents} mandats={mandats} activeSubscription={activeSubscription} setActiveTab={setActiveTab} missionStatus={missionStatus} />}
        {activeTab === 'missions' && <MissionsTab setActiveTab={setActiveTab} />}
        {activeTab === 'payment' && <PaymentTab user={user} onPaymentSuccess={handlePaymentSuccess} />}
        {activeTab === 'mission-form' && <MissionFormTab user={user} activeSubscription={activeSubscription} setActiveTab={setActiveTab} />}
        {activeTab === 'mandat' && <MandatTab mandats={mandats} setMandats={setMandats} user={user} activeSubscription={activeSubscription} />}
        {activeTab === 'documents' && <DocumentsTab documents={documents} setDocuments={setDocuments} onPanicRoom={handlePanicRoom} activeSubscription={activeSubscription} />}
        {activeTab === 'audit' && <AuditTab documents={documents} activeSubscription={activeSubscription} onDocumentUploaded={async () => {
          const token = localStorage.getItem('token');
          const docsRes = await axios.get(`${API}/documents`, { headers: { 'Authorization': `Bearer ${token}` }, withCredentials: true });
          setDocuments(docsRes.data.documents || []);
        }} />}
        {activeTab === 'proofs' && <ProofsTab />}
        {activeTab === 'referral' && <ReferralTab user={user} />}
        {activeTab === 'assistant' && (
          <div className="space-y-6">
            <div>
              <h2 className="font-orbitron text-2xl md:text-3xl font-bold text-white mb-2">Assistant du Bug</h2>
              <p className="text-gray-400 font-mono text-sm">// VOTRE_EXPERT_PROTECTEUR</p>
            </div>
            <div className="h-[600px]">
              <AssistantDuBug isOpen={true} onClose={() => {}} isFloating={false} />
            </div>
          </div>
        )}
        {activeTab === 'data-security' && <DataSecurityTab user={user} />}
        {activeTab === 'support' && <SupportTab />}
        
        
        {/* Footer Signature Toulouse uniquement sur OverviewTab */}
      </main>
      
      {/* Floating Chatbot Button - only show when not on assistant tab */}
      {activeTab !== 'assistant' && (
        <AssistantDuBug 
          isOpen={chatbotOpen} 
          onClose={() => setChatbotOpen(!chatbotOpen)} 
          isFloating={true} 
        />
      )}
      
      {/* Bulle proactive chatbot pour nouveaux utilisateurs */}
      <ProactiveChatBubble 
        variant="dashboard" 
        onOpenChat={() => setChatbotOpen(true)} 
      />
      
      {/* Google Review Popup - shown when mission is closed */}
      <GoogleReviewPopup 
        isOpen={showReviewPopup} 
        onClose={() => setShowReviewPopup(false)} 
        userName={user?.name}
      />
    </div>
  );
}
